import type { DocumentFile } from "@shared/operations-schema";
import { useDocuments } from "@/hooks/use-documents";
import { useCurrentBusiness } from "@/hooks/use-businesses";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UploadDocumentDialog } from "./upload-document-dialog";
import { 
  DownloadIcon, 
  FileTextIcon, 
  FileIcon, 
  FileSpreadsheetIcon, 
  FileImageIcon, 
  TrashIcon, 
  EyeIcon,
  Loader2
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/lib/utils";

interface DocumentListProps {
  transactionId?: number;
}

export function DocumentList({ transactionId }: DocumentListProps) {
  const { currentBusiness } = useCurrentBusiness();
  const businessId = currentBusiness?.id;
  
  const { 
    documents = [], 
    isLoading, 
    error, 
    getDownloadUrl,
    deleteDocument,
    isDeleting
  } = useDocuments(businessId, transactionId);

  const filteredDocuments = documents;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-red-500">Error loading documents: {error.message}</p>
      </div>
    );
  }

  if (filteredDocuments.length === 0) {
    return (
      <div className="py-8 text-center">
        <FileIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">No documents found</h3>
        <p className="text-muted-foreground mb-6">
          Upload documents to keep track of important files.
        </p>
        <UploadDocumentDialog transactionId={transactionId} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Documents</h2>
        <UploadDocumentDialog transactionId={transactionId} />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocuments.map((document) => (
          <DocumentCard 
            key={document.id} 
            document={document}
            onDelete={deleteDocument}
            isDeleting={isDeleting}
            downloadUrl={getDownloadUrl(document.id)}
            businessId={businessId}
          />
        ))}
      </div>
    </div>
  );
}

interface DocumentCardProps {
  document: DocumentFile;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  downloadUrl: string;
  businessId?: number;
}

function DocumentCard({ 
  document, 
  onDelete, 
  isDeleting,
  downloadUrl
}: DocumentCardProps) {

  // Get file icon based on file type
  const getFileIcon = () => {
    const fileType = document.fileType?.toLowerCase();
    
    if (fileType?.includes('pdf')) {
      return <FileTextIcon className="h-10 w-10 text-red-500" />;
    } else if (fileType?.includes('image')) {
      return <FileImageIcon className="h-10 w-10 text-blue-500" />;
    } else if (fileType?.includes('spreadsheet') || fileType?.includes('excel') || fileType?.includes('csv')) {
      return <FileSpreadsheetIcon className="h-10 w-10 text-green-500" />;
    } else {
      return <FileIcon className="h-10 w-10 text-gray-500" />;
    }
  };
  
  // Format file size (convert bytes to KB or MB)
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) {
      return bytes + ' bytes';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
  };
  
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-4 text-base font-medium">
          {getFileIcon()}
          <div className="truncate">{document.name}</div>
        </CardTitle>
        <CardDescription className="mt-1">
          {formatFileSize(document.size || 0)} • {formatDate(document.uploadedAt || new Date().toISOString())}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pb-2">
        {document.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
            {document.description}
          </p>
        )}
        
        {document.transactionId && (
          <div className="mt-2">
            <span className="text-xs font-medium bg-muted text-muted-foreground px-2 py-1 rounded-full">
              Linked to Transaction
            </span>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="pt-2">
        <div className="flex justify-between items-center w-full">
          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8">
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Document</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this document? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => onDelete(document.id)}
                    disabled={isDeleting}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {isDeleting ? 
                      <Loader2 className="h-4 w-4 animate-spin mr-2" /> : 
                      <TrashIcon className="h-4 w-4 mr-2" />
                    }
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            

          </div>
          
          <Button variant="outline" size="sm" asChild>
            <a href={downloadUrl} download target="_blank" rel="noopener noreferrer">
              <DownloadIcon className="h-4 w-4 mr-2" />
              Save to Computer
            </a>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
